import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Headphones, Phone, Database, ArrowRightLeft, Cloud, GitBranch, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Headphones,
  Phone,
  Database,
  ArrowRightLeft,
  Cloud,
  GitBranch
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedProducts = products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <h1 className="text-5xl font-bold text-slate-900" data-testid="products-page-heading">
              Enterprise Products
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              A detailed catalogue of Digian.ai products that power our Enterprise Solutions across data discovery, preparation, analysis, and AI voice experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-20">
            {Object.entries(groupedProducts).map(([category, categoryProducts], catIndex) => (
              <div key={category} data-testid={`product-category-${catIndex}`}>
                <h2 className="text-3xl font-bold text-slate-900 mb-12">{category}</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {categoryProducts.map((product, index) => {
                    const Icon = iconMap[product.icon] || Database;
                    return (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        data-testid={`product-card-${product.id}`}
                      >
                        <div className="h-full bg-white rounded-xl border border-slate-200 p-8 hover:border-indigo-500 transition-all duration-300 hover-lift">
                          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center mb-6">
                            <Icon className="w-7 h-7 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 mb-3">{product.name}</h3>
                          <p className="text-slate-600 leading-relaxed mb-6">{product.description}</p>
                          
                          <div className="space-y-3 mb-6">
                            {product.features.slice(0, 3).map((feature, idx) => (
                              <div key={idx} className="flex items-start space-x-2">
                                <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <div className="w-2 h-2 rounded-full bg-indigo-600"></div>
                                </div>
                                <span className="text-sm text-slate-600">{feature}</span>
                              </div>
                            ))}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <Link to="/ai-voice-agent" data-testid={`view-details-btn-${product.id}`}>
                              <Button variant="outline" size="sm" className="w-full">
                                View Details
                              </Button>
                            </Link>
                            <Link to="/demo" data-testid={`request-demo-btn-${product.id}`}>
                              <Button size="sm" className="w-full">
                                Get Demo
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-4xl font-bold text-white" data-testid="products-cta-heading">
              Need a Custom Solution?
            </h2>
            <p className="text-xl text-slate-300">
              Our team can build tailored AI and data solutions for your specific needs
            </p>
            <Link to="/contact" data-testid="products-cta-contact-btn">
              <Button size="lg" variant="secondary">
                Contact Our Team
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;